<template>
  <div ref="mapRef" class="map">
    <slot v-if="mapIsLoaded" />
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions, mapGetters } from 'vuex'
import 'mapbox-gl/dist/mapbox-gl.css'
import { SET_MAP_IS_LOADED, SET_CLICKED_ITEM_TYPE } from '@/store/mutation-types'
import { RESET_ALL_SELECTIONS, RESET_PLANVIEW_SELECTIONS } from '@/store/action-types'
import { getCoordinatesFromQueryParams } from '@/utils/query-param-utils'
import { getBoundsForCoordinates } from '@/utils/coordinates'
import useMapbox from '@/composables/useMapbox'
import { useSelectionStore } from '@/pinia/selectionNew'

export default {
  name: 'MapContainer',
  props: {
    initCoordinates: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      resizer: null
    }
  },
  computed: {
    ...mapState({
      mapIsLoaded: (state) => state.mapIsLoaded,
      clickedItemType: (state) => state.clickedItemType
    }),
    ...mapGetters({
      isDrawPointActive: 'isDrawPointActive',
      isMapviewShowing: 'isMapviewShowing',
      isMeasurementActive: 'isMeasurementActive',
      isPlanviewShowing: 'isPlanviewShowing',
      isModelviewShowing: 'isModelviewShowing'
    }),
    mapboxTranslations() {
      return {
        'AttributionControl.ToggleAttribution': this.$t('mapbox__toggle__attribution'),
        'AttributionControl.MapFeedback': this.$t('mapbox__map__feedback'),
        'FullscreenControl.Enter': this.$t('mapbox__enter'),
        'FullscreenControl.Exit': this.$t('mapbox__exit'),
        'GeolocateControl.FindMyLocation': this.$t('mapbox__find__my__location'),
        'GeolocateControl.LocationNotAvailable': this.$t('mapbox__location__not_available'),
        'LogoControl.Title': this.$t('mapbox__logo__control__title'),
        'NavigationControl.ResetBearing': this.$t('mapbox__navigation__control__reset'),
        'NavigationControl.ZoomIn': this.$t('mapbox__zoom__in'),
        'NavigationControl.ZoomOut': this.$t('mapbox__zoom__out'),
        'ScaleControl.Feet': 'ft',
        'ScaleControl.Meters': 'm',
        'ScaleControl.Kilometers': 'km',
        'ScaleControl.Miles': 'mi',
        'ScaleControl.NauticalMiles': 'nm',
        'ScrollZoomBlocker.CtrlMessage': this.$t('mapbox__scroll__zoom__blocker__ctrl__message'),
        'ScrollZoomBlocker.CmdMessage': this.$t('mapbox__scroll__zoom__blocker__cmd__message'),
        'TouchPanBlocker.Message': this.$t('mapbox__touch__pan__blocker__message')
      }
    }
  },
  mounted() {
    const { lat, lng } = getCoordinatesFromQueryParams(location.search)
    const bounds = getBoundsForCoordinates(this.initCoordinates)
    const { map, container } = useMapbox()
    this.$refs.mapRef.appendChild(container)
    useSelectionStore()
    if (!(lat && lng))
      map.fitBounds(bounds, {
        padding: { left: 200, right: 100, top: 100, bottom: 100 },
        maxZoom: 15,
        maxDuration: 0.01
      })
    this.addResizeObserver(map)
    map.on('click', this.onClick)
  },
  beforeUnmount() {
    this.$store?.map?.off('click', this.onClick)
    this.resizer?.disconnect()
    this.$store?.map?.remove()
  },
  methods: {
    ...mapActions({
      resetAllSelections: RESET_ALL_SELECTIONS,
      resetPlanviewSelections: RESET_PLANVIEW_SELECTIONS
    }),
    ...mapMutations({
      setMapIsLoaded: SET_MAP_IS_LOADED,
      commitClick: SET_CLICKED_ITEM_TYPE
    }),
    addResizeObserver(map) {
      const mapDiv = document.getElementById('mapContainer')
      this.resizer = new ResizeObserver(() => {
        map.resize()
      }, 100)
      this.resizer.observe(mapDiv)
    },
    onClick(event) {
      if (this.isDrawPointActive || this.isMeasurementActive) return
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      if (!renderedFeatures.length || renderedFeatures[0].source === 'composite') {
        if (this.isMapviewShowing) {
          this.commitClick({ type: 'region' })
          this.resetAllSelections()
        } else if (this.isPlanviewShowing || this.isModelviewShowing) {
          this.commitClick({ type: 'level' })
          this.resetPlanviewSelections()
        }
      }
    }
  }
}
</script>

<style scoped>
.map {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
